/**
 * The only code that assigns into an `Entry` (ADR 0012:75-78).
 *
 * A caller outside `document/` passes a patch and never holds a live container, so the write shapes
 * that lose data cannot be expressed — **the operand disappears**. That matters because the type
 * system does not reject them: `tsc --strict` accepts `f({ ...entry.metadata, k: v })`, since object
 * spread drops the index signature.
 *
 * ## What a patch means
 *
 * A key **present** in the patch is acted on; a key absent is left alone. `undefined` as the value
 * of a present key means *clear this field* — and clearing is `delete`, not `= null`.
 *
 * That is a merge decision, not a mapping detail (#367, F11-F12; #381). Measured over 200 rounds
 * against this project's own `@automerge/automerge@3.2.4`, one device clearing a field while another
 * sets it: `delete` yields to the set **200/200**, deterministically, by the documented map merge
 * rule. `= null` is itself a set, so it races — it won 88 and lost 112, decided by internal
 * operation id rather than by wall-clock time. The decided semantics are that **a clear yields to a
 * concurrent edit**, so a patch clears with `delete`.
 *
 * **Only genuinely optional fields may be cleared.** Five non-optional `X | null` fields sit outside
 * the metadata bag — `Entry.projectId` and `Entry.areaId`, `Topic.parentId` and `Topic.entryType`,
 * `Project.areaId` (#382). For those `delete` is not a different merge outcome, it is a document
 * that violates its own declared type. They take assignment, `null` included. Extending the bag's
 * `undefined` → `delete` mapping to them is the named trap here.
 *
 * ## Why a container is written in place
 *
 * Assigning a map, a list, or a string sets a key to a new value, which enters the map merge rule's
 * lottery with the whole container as the stake. So a nested value is diffed into the container that
 * is already there rather than assigned over it. Three shapes in the tree got this wrong and two of
 * them were the *careful*-looking form:
 *
 * - **The whole-container spread** loses the other device's edits, and loses them asymmetrically:
 *   the spreading device wins in both merge directions, so one remaining violation defeats every
 *   correct write to that container (#108, #367 F1).
 * - **`splice(0, len)` then re-push** reads as the safe alternative to whole-array assignment and is
 *   worse. Two devices saving an **unchanged** list produce `["t1","t2","t1","t2"]` — it corrupts
 *   where assignment merely loses (#367 F2). `applyList` below is the minimal diff, which keeps both.
 * - **Rebuilding a list of objects** to change one field drops the other device's edit to a
 *   different element. `useTaskMutations.ts` justified it with "Automerge requires rebuilding
 *   arrays/objects inside the transaction", which the Lists reference contradicts in as many words:
 *   *"please don't do this in Automerge"*. The true constraint is only that an outside array cannot
 *   be handed over as-is, and copying it *into* the existing list satisfies that (#367 F4).
 *
 * ## What is not settled here
 *
 * `Entry.title` and `Entry.content` are collaborative text, and assignment replaces the whole text
 * object — one device's journal edit is dropped whole, and the history grows 18.9x (#367 F8, 18
 * sites). `A.updateText` merges those same edits. It is not done here: it changes behaviour, it is
 * not in this ticket, and routing every field write through this module is what makes it a change in
 * one place when it is ticketed.
 */

import type { AppDocument, Entry } from '../schema'

/**
 * A patch over an entry's `metadata` bag.
 *
 * Values are whatever the kind stores. `undefined` clears the key; the bag's own keys are all
 * optional, so every one of them may be cleared.
 */
export type MetadataPatch = Record<string, unknown>

/**
 * A patch over an entry's own fields.
 *
 * `summary`, `imageUrl`, `url` and `archivedAt` are optional on `Entry` and may be cleared by
 * passing `undefined`. `projectId` and `areaId` are **not** optional — they are `string | null` and
 * take assignment, so passing `undefined` for them leaves them alone rather than deleting them
 * (#382).
 */
export interface EntryPatch {
  title?: string
  content?: string
  summary?: string
  imageUrl?: string
  url?: string
  archivedAt?: string
  projectId?: string | null
  areaId?: string | null
  topicIds?: string[]
  metadata?: MetadataPatch
}

/** The `Entry` fields that are optional on the type, and so may be cleared with `delete`. */
const CLEARABLE_ENTRY_FIELDS = new Set<keyof EntryPatch>([
  'summary',
  'imageUrl',
  'url',
  'archivedAt',
])

/**
 * Apply a patch to an entry, in place.
 *
 * Does not touch `updatedAt`; `operations/entries.ts` owns that, because whether a write counts as
 * an edit is the operation's question rather than the patch's.
 */
export function applyEntryPatch(entry: Entry, patch: EntryPatch): void {
  for (const key of Object.keys(patch) as (keyof EntryPatch)[]) {
    const next = patch[key]

    if (key === 'metadata') {
      if (next !== undefined) {
        applyMetadataPatch(entry.metadata, next as MetadataPatch)
      }
      continue
    }

    if (key === 'topicIds') {
      if (next !== undefined) applyList(entry.topicIds, next as string[])
      continue
    }

    if (next === undefined) {
      // A non-clearable field passed `undefined` means "leave it alone", which is what every
      // caller's `if (data.x !== undefined)` guard meant before this module existed.
      if (CLEARABLE_ENTRY_FIELDS.has(key)) delete entry[key as ClearableField]
      continue
    }

    assignScalar(entry as unknown as Record<string, unknown>, key, next)
  }
}

type ClearableField = 'summary' | 'imageUrl' | 'url' | 'archivedAt'

/**
 * Apply a patch to a metadata bag, in place.
 *
 * Exported because `operations/` writes metadata on containers other than an entry is not a case
 * that exists yet — it is here so a test can reach the walk directly.
 */
export function applyMetadataPatch(
  metadata: Record<string, unknown>,
  patch: MetadataPatch,
): void {
  for (const key of Object.keys(patch)) {
    const next = patch[key]

    if (next === undefined) {
      delete metadata[key]
      continue
    }

    applyValue(metadata, key, next)
  }
}

/**
 * Write one value into a container, in place wherever the value is itself a container.
 */
function applyValue(
  container: Record<string, unknown>,
  key: string,
  next: unknown,
): void {
  const current = container[key]

  if (Array.isArray(next)) {
    if (Array.isArray(current)) {
      applyList(current, next)
    } else {
      container[key] = next.map(forDocument)
    }
    return
  }

  if (isPlainObject(next)) {
    if (isPlainObject(current)) {
      applyObject(current, next)
    } else {
      container[key] = forDocument(next)
    }
    return
  }

  assignScalar(container, key, next)
}

/**
 * Assign a scalar, skipping a write that changes nothing.
 *
 * Every write is an operation, and two devices writing the identical value still record a conflict
 * (#367 F9). Not writing what has not changed costs nothing and removes that noise.
 */
function assignScalar(
  container: Record<string, unknown>,
  key: string,
  next: unknown,
): void {
  if (container[key] !== next) container[key] = next
}

/**
 * Make `current` hold what `next` holds, editing the existing list rather than replacing it.
 *
 * Elements carrying an `id` are matched on it, so an element edited on this device and an element
 * edited on another survive together. Kept elements hold their position; new ones are appended,
 * which is what makes this a diff rather than a rebuild.
 */
function applyList(current: unknown[], next: readonly unknown[]): void {
  if (next.every(isKeyed) && current.every(isKeyed)) {
    applyKeyedList(current as KeyedElement[], next as readonly KeyedElement[])
    return
  }

  for (let i = current.length - 1; i >= 0; i--) {
    if (!next.includes(current[i])) current.splice(i, 1)
  }

  for (const value of next) {
    if (!current.includes(value)) current.push(forDocument(value))
  }
}

interface KeyedElement {
  id: string
  [key: string]: unknown
}

function isKeyed(value: unknown): value is KeyedElement {
  return isPlainObject(value) && typeof value.id === 'string'
}

function applyKeyedList(
  current: KeyedElement[],
  next: readonly KeyedElement[],
): void {
  const nextIds = new Set(next.map((element) => element.id))

  for (let i = current.length - 1; i >= 0; i--) {
    if (!nextIds.has(current[i].id)) current.splice(i, 1)
  }

  for (const element of next) {
    const existing = current.find((candidate) => candidate.id === element.id)
    if (existing) {
      applyObject(existing, element)
    } else {
      current.push(forDocument(element) as KeyedElement)
    }
  }
}

/**
 * Make `current` hold what `next` holds, key by key, without replacing the map.
 *
 * A key `next` does not carry is removed, because `next` is a value rather than a patch — the patch
 * layer is `applyMetadataPatch`, and this is what it means to store the value it was handed.
 */
function applyObject(
  current: Record<string, unknown>,
  next: Record<string, unknown>,
): void {
  for (const key of Object.keys(current)) {
    if (!(key in next) || next[key] === undefined) delete current[key]
  }

  for (const key of Object.keys(next)) {
    if (next[key] === undefined) continue
    applyValue(current, key, next[key])
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype ||
      Object.getPrototypeOf(value) === null)
  )
}

/**
 * Copy a value into a form Automerge accepts.
 *
 * Automerge throws on `undefined` — `RangeError: Cannot assign undefined value … because
 * 'undefined' is not a valid JSON data type` — and it throws in three places, not one: on
 * assignment, inside `A.from`, and on an object literal containing an `undefined` key (#367 F10).
 * Dropping those keys here is why no caller has to remember the conditional-spread idiom.
 */
function forDocument(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(forDocument)

  if (isPlainObject(value)) {
    const copy: Record<string, unknown> = {}
    for (const key of Object.keys(value)) {
      if (value[key] === undefined) continue
      copy[key] = forDocument(value[key])
    }
    return copy
  }

  return value
}

/**
 * Copy a value built outside the document into it.
 *
 * The one thing `operations/` needs from the `undefined`-stripping walk above, for the create paths
 * that build a whole entity rather than patching one.
 */
export function valueForDocument<T>(value: T): T {
  return forDocument(value) as T
}

/**
 * The document containers an operation may write into, named once.
 *
 * `trust` and `sharedEntries` are optional on `AppDocument` — a document created before they
 * existed carries neither — so an operation writing one has to create it (see `setTrust`).
 */
export type DocumentContainer = keyof Pick<
  AppDocument,
  | 'entries'
  | 'topics'
  | 'areas'
  | 'projects'
  | 'links'
  | 'trust'
  | 'sharedEntries'
>
